// frontend2/src/pages/ShopReport/ShopWalletReport.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TablePagination,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
  Tooltip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWallet,
  faArrowUp,
  faArrowDown,
  faRefresh,
  faStore,
  faReceipt,
  faCalendarAlt,
  faFilter,
  faSearch,
  faPlusCircle,
  faMinusCircle,
  faUserShield,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { maskVpa } from '../../utils/maskVpa';
// [CASH-MODE] UPI / Cash filter + indication (credits / admin rows have no mode -> '-')
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, paymentModeLabel } from '../../utils/paymentMode';

// Shop dropdown search: shop/seller name, merchant ID (shop code), mobile, city or pincode
const filterShopOptions = (options, inputValue) => {
  const q = (inputValue || '').toLowerCase().trim();
  if (!q) return options;
  const digits = q.replace(/\D/g, '');
  return options.filter((o) => {
    const text = [o.merchantId, o.businessName, o.sellerName, o.mobileNumber, o.city, o.pincode]
      .map((v) => String(v || '').toLowerCase())
      .join(' ');
    if (text.includes(q)) return true;
    // "98765 43210" / "+91 98765..." still matches a stored mobile number
    return digits.length >= 4 && String(o.mobileNumber || '').replace(/\D/g, '').includes(digits);
  });
};

const ShopWalletReport = () => {
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState({
    totalWalletBalance: 0,
    totalCredits: 0,
    totalDebits: 0,
    adminActionCount: 0,
    accessibleShopsCount: 0
  });
  const [shopsList, setShopsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userLevel, setUserLevel] = useState(null);
  const { can } = usePermissions();
  const canAdjust = can('merchants.advanceFund.adjust');
  const canExport = can('merchants.advanceFund.export');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Filters & Search
  const todayStr = new Date().toISOString().split('T')[0];
  const thirtyDaysAgoStr = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(thirtyDaysAgoStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [transactionType, setTransactionType] = useState('all');
  const [selectedShopId, setSelectedShopId] = useState('');
  const [pincodeFilter, setPincodeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // typed text; copied to searchQuery after a short pause
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [debitsByMode, setDebitsByMode] = useState(null); // [CASH-MODE]

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [paginationInfo, setPaginationInfo] = useState({ totalRecords: 0 });

  // Admin Action Modal State (Level 1 only)
  const [openAdminModal, setOpenAdminModal] = useState(false);
  const [actionShopId, setActionShopId] = useState('');
  const [actionType, setActionType] = useState('credit');
  const [actionAmount, setActionAmount] = useState('');
  const [actionRemarks, setActionRemarks] = useState('');
  const [actionError, setActionError] = useState('');

  const token = sessionStorage.getItem('token');

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserLevel(decoded.level);
      } catch (err) {
        console.error('Error decoding token:', err);
      }
    }
  }, [token]);

  const fetchWalletReport = useCallback(async () => {
    if (!token) {
      setError('Authentication token missing. Please login again.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        startDate,
        endDate,
        page: page + 1,
        limit: rowsPerPage,
        transactionType: transactionType === 'all' ? undefined : transactionType,
        shopId: selectedShopId || undefined,
        pincode: pincodeFilter.trim() || undefined,
        search: searchQuery.trim() || undefined,
        paymentMode: paymentModeParam(paymentMode) // [CASH-MODE] undefined for ALL
      };

      const response = await axios.post(
        `${API_BASE_URL}/service-advance/report`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200 && response.data && response.data.success) {
        setReportData(response.data.data || []);
        setTotals(response.data.totals || {
          totalWalletBalance: 0,
          totalCredits: 0,
          totalDebits: 0,
          adminActionCount: 0,
          accessibleShopsCount: 0
        });
        setShopsList(response.data.shops || []);
        setPaginationInfo(response.data.pagination || { totalRecords: 0 });
        // [CASH-MODE] debit split, only when the API sends it
        setDebitsByMode(
          response.data.debitsByMode ||
          (response.data.totals && response.data.totals.debitsByMode) ||
          response.data.totalsByMode ||
          null
        );
      } else {
        setError((response.data && response.data.message) || 'Failed to fetch wallet report.');
      }
    } catch (err) {
      console.error('Error fetching wallet report:', err);
      const errMsg = (err.response && err.response.data && err.response.data.message) || 'Error connecting to server.';
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  }, [token, startDate, endDate, transactionType, selectedShopId, pincodeFilter, searchQuery, page, rowsPerPage, paymentMode]); // [CASH-MODE] paymentMode

  useEffect(() => {
    fetchWalletReport();
  }, [fetchWalletReport]);

  // Debounce the search box so the report is not re-fetched on every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setPage(0);
        setSearchQuery(searchInput);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput, searchQuery]);

  const handleOpenAdminModal = () => {
    if (!canAdjust) return;
    setActionShopId(selectedShopId || (shopsList[0] ? shopsList[0].shopId : ''));
    setActionType('credit');
    setActionAmount('');
    setActionRemarks('');
    setActionError('');
    setOpenAdminModal(true);
  };

  const handleQuickShopAction = (targetShopId, type) => {
    if (!canAdjust) return;
    setActionShopId(targetShopId);
    setActionType(type);
    setActionAmount('');
    setActionRemarks('');
    setActionError('');
    setOpenAdminModal(true);
  };

  const handleCloseAdminModal = () => {
    setOpenAdminModal(false);
  };

  const handleProcessAdminAction = async (e) => {
    e.preventDefault();
    setActionError('');
    if (!canAdjust) {
      setActionError('You do not have permission to adjust merchant wallets.');
      return;
    }

    if (!actionShopId) {
      setActionError('Please select a shop.');
      return;
    }

    const amt = parseFloat(actionAmount);
    if (!amt || amt <= 0) {
      setActionError('Please enter a valid positive amount.');
      return;
    }

    if (!actionRemarks || actionRemarks.trim().length === 0) {
      setActionError('Remarks are required for admin audit log.');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/service-advance/admin-action`,
        {
          shopId: actionShopId,
          actionType,
          amount: amt,
          remarks: actionRemarks.trim()
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200 && response.data && response.data.success) {
        setSuccessMessage(response.data.message || `Successfully processed ${actionType} of ₹${amt}!`);
        handleCloseAdminModal();
        fetchWalletReport();

        setTimeout(() => {
          setSuccessMessage('');
        }, 6000);
      } else {
        setActionError((response.data && response.data.message) || 'Failed to process admin action.');
      }
    } catch (err) {
      console.error('Error processing admin wallet action:', err);
      const errMsg = (err.response && err.response.data && err.response.data.message) || 'Server error during wallet action.';
      setActionError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportCSV = () => {
    if (!canExport) return;
    if (!reportData || reportData.length === 0) {
      setError('No wallet transaction data available to export.');
      return;
    }

    const headers = [
      'Sl No',
      'Date & Time',
      'Merchant ID',
      'Merchant Name',
      'Shop Name',
      'Mobile Number',
      'City',
      'Pincode',
      'Type',
      'Mode', // [CASH-MODE]
      'Amount (INR)',
      'Balance After (INR)',
      'Description / Remarks',
      'Ref ID / Txn No',
      'Action Tag',
      'Performed By'
    ];

    const csvRows = [headers.join(',')];

    reportData.forEach((row, idx) => {
      const sl = row.serialNumber || idx + 1;
      const dateVal = row.createdAt ? new Date(row.createdAt).toLocaleString('en-IN') : 'N/A';
      const mId = row.merchantId || (row.shop?.merchantId) || 'N/A';
      const mName = row.merchantName || (row.shop?.sellerName) || 'N/A';
      const sName = row.shopName || (row.shop?.businessName) || 'N/A';
      const mob = row.mobileNumber || (row.shop?.mobileNumber) || 'N/A';
      const city = row.shop?.city || 'N/A';
      const pin = row.shop?.pincode || 'N/A';
      const type = (row.type || 'credit').toUpperCase();
      const amt = row.amount || 0;
      const balAfter = row.balanceAfter || 0;
      const desc = row.description || '';
      const refId = row.refId || '';
      const tag = row.isAdminAction ? 'Admin Action' : 'System / App';
      const perfBy = row.performedByName || (row.isAdminAction ? 'Admin' : 'System');

      csvRows.push([
        sl,
        `"${dateVal}"`,
        `"${mId}"`,
        `"${mName}"`,
        `"${sName}"`,
        `"${mob}"`,
        `"${city}"`,
        `"${pin}"`,
        `"${type}"`,
        `"${row.paymentMode ? paymentModeLabel(row) : '-'}"`, // [CASH-MODE]
        amt,
        balAfter,
        `"${desc}"`,
        `"${refId}"`,
        `"${tag}"`,
        `"${perfBy}"`
      ].join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Shop_Wallet_Report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getUserLevelText = (lvl) => {
    switch (lvl) {
      case 1: return 'Super Admin';
      case 2: return 'State Admin';
      case 3: return 'State Admin Staff';
      case 4: return 'MSB';
      case 5: return 'MSB Staff';
      default: return 'User';
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <Box sx={{ position: 'relative', padding: 2 }}>
        <BackButton />
        <br /><br />

        {/* Header Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FontAwesomeIcon icon={faWallet} style={{ color: '#0284c7' }} />
            Shop Wallet / Service Advance Report
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {canExport && (
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              sx={{ fontWeight: 700, height: 40 }}
            >
              Export CSV
            </Button>
            )}
            <IconButton color="primary" onClick={fetchWalletReport} title="Refresh Data">
              <FontAwesomeIcon icon={faRefresh} />
            </IconButton>
            <Chip 
              label={`Logged in: ${getUserLevelText(userLevel)}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </Box>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {successMessage}
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body1">{error}</Typography>
          </Alert>
        )}

        {/* Summary Metric Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={3} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', color: '#ffffff' }}>
              <CardContent>
                <Typography variant="overline" sx={{ letterSpacing: 1, fontWeight: 700, opacity: 0.9 }}>
                  Total Wallet Holding
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 1 }}>
                  {formatCurrency(totals.totalWalletBalance)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
                  Across {totals.accessibleShopsCount || 0} Shops (Pincode Scoped)
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  <FontAwesomeIcon icon={faArrowUp} style={{ color: '#16a34a', marginRight: '6px' }} />
                  Total Wallet Credits
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#16a34a', mt: 1 }}>
                  {formatCurrency(totals.totalCredits)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Self Top-ups & Admin Credits
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  <FontAwesomeIcon icon={faArrowDown} style={{ color: '#dc2626', marginRight: '6px' }} />
                  Total Wallet Debits
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#dc2626', mt: 1 }}>
                  {formatCurrency(totals.totalDebits)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Commission Deductions & Reductions
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  <FontAwesomeIcon icon={faUserShield} style={{ color: '#9333ea', marginRight: '6px' }} />
                  Admin Actions
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#9333ea', mt: 1 }}>
                  {totals.adminActionCount || 0}
                </Typography>

                {canAdjust ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    startIcon={<FontAwesomeIcon icon={faPlusCircle} />}
                    onClick={handleOpenAdminModal}
                    sx={{ mt: 1, fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
                  >
                    Add / Reduce Wallet
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Read-only view for your pincodes
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* [CASH-MODE] Debits split by UPI / Cash (shown only when the API sends it) */}
        {debitsByMode && (
          <PaymentModeTotals
            totals={debitsByMode}
            amountKey={
              (() => {
                const t = debitsByMode.UPI || debitsByMode.CASH || {};
                if (t.amount !== undefined) return 'amount';
                return Object.keys(t).find((k) => k !== 'count' && typeof t[k] === 'number') || 'amount';
              })()
            }
            title="Debits"
            sx={{ mb: 3 }}
          />
        )}

        {/* Filter Section */}
        <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <FontAwesomeIcon icon={faFilter} style={{ color: '#0284c7' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Search & Filter Wallet Transactions
            </Typography>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="Search Shop / Ref / Description"
                size="small"
                fullWidth
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Shop name, merchant ID, mobile, pincode..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FontAwesomeIcon icon={faSearch} style={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={2}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={2}>
              <TextField
                label="End Date"
                type="date"
                size="small"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={transactionType}
                  label="Type"
                  onChange={(e) => setTransactionType(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="credit">Credits (Top-ups)</MenuItem>
                  <MenuItem value="debit">Debits (Deductions)</MenuItem>
                  <MenuItem value="adminAction">Admin Actions</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4} md={2}>
              {/* Searchable shop filter: type shop name, merchant ID / shop code, seller or mobile */}
              <Autocomplete
                size="small"
                fullWidth
                options={shopsList}
                value={shopsList.find((s) => s.shopId === selectedShopId) || null}
                onChange={(event, newValue) => {
                  setPage(0);
                  setSelectedShopId(newValue ? newValue.shopId : '');
                }}
                isOptionEqualToValue={(option, value) => option.shopId === value.shopId}
                getOptionLabel={(s) =>
                  `${s.merchantId ? `[${s.merchantId}] ` : ''}${s.businessName} - ${s.sellerName} (${s.pincode})`
                }
                filterOptions={(options, state) => filterShopOptions(options, state.inputValue)}
                renderOption={(props, s) => (
                  <li {...props} key={s.shopId}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {s.merchantId ? `[${s.merchantId}] ` : ''}{s.businessName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.sellerName} | Mob: {s.mobileNumber || 'N/A'} | {s.pincode}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Filter Shop" placeholder="All Shops - type to search" />
                )}
                noOptionsText="No matching shops"
              />
            </Grid>

            {/* [CASH-MODE] Payment Mode filter */}
            <Grid item xs={12} sm={4} md={2}>
              <PaymentModeFilter
                value={paymentMode}
                onChange={(v) => { setPage(0); setPaymentMode(v); }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={4} md={1}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => { setPage(0); fetchWalletReport(); }}
                sx={{ borderRadius: 2, height: '40px' }}
              >
                Apply
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Report Transactions Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress color="primary" size={50} />
            <Typography variant="body1" sx={{ ml: 2 }}>Loading Wallet Report transactions...</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
            <Table aria-label="Shop Wallet Report Transactions Table">
              <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Sl.No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Merchant ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Merchant Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Shop Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Mobile</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  {/* [CASH-MODE] */}
                  <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Current Balance</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Description / Remarks</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Action Tag</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Performed By</TableCell>
                  {canAdjust && (
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Admin Quick Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:hover': { backgroundColor: '#f1f5f9' },
                      '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                      ...(row.isAdminAction && { backgroundColor: '#faf5ff' })
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                        {row.serialNumber}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {new Date(row.createdAt).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {row.merchantId || (row.shop?.merchantId) || 'N/A'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.merchantName || (row.shop?.sellerName) || 'N/A'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0284c7' }}>
                        <FontAwesomeIcon icon={faStore} style={{ marginRight: '6px', color: '#0284c7' }} />
                        {row.shopName || (row.shop?.businessName) || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.shop?.city ? `${row.shop.city} (${row.shop.pincode})` : ''}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {row.mobileNumber || (row.shop?.mobileNumber) || 'N/A'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {row.type === 'credit' ? (
                        <Chip
                          icon={<FontAwesomeIcon icon={faArrowUp} />}
                          label="CREDIT"
                          color="success"
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      ) : (
                        <Chip
                          icon={<FontAwesomeIcon icon={faArrowDown} />}
                          label="DEBIT"
                          color="error"
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      )}
                    </TableCell>

                    {/* [CASH-MODE] '-' when the row has no payment mode */}
                    <TableCell>
                      {row.paymentMode ? <PaymentModeChip row={row} /> : '-'}
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 800,
                          color: row.type === 'credit' ? '#16a34a' : '#dc2626'
                        }}
                      >
                        {row.type === 'credit' ? `+ ${formatCurrency(row.amount)}` : `- ${formatCurrency(row.amount)}`}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0284c7' }}>
                        {formatCurrency(row.balanceAfter)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {row.description || 'N/A'}
                      </Typography>
                      {row.refId && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {row.refLabel || 'Ref'}: {row.refId}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {row.isAdminAction ? (
                        <Chip
                          icon={<FontAwesomeIcon icon={faUserShield} />}
                          label="Admin Action"
                          color="secondary"
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      ) : (
                        <Chip
                          label="System / App"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {row.performedByName || 'System'}
                      </Typography>
                    </TableCell>

                    {canAdjust && (
                      <TableCell align="center">
                        {row.shop ? (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title={`Add Money (Credit) to ${row.shop.businessName}`}>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() => handleQuickShopAction(row.shop.shopId, 'credit')}
                                sx={{
                                  minWidth: 'auto',
                                  px: 1.5,
                                  py: 0.5,
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  boxShadow: '0 2px 8px rgba(22, 163, 74, 0.3)'
                                }}
                                startIcon={<FontAwesomeIcon icon={faPlusCircle} />}
                              >
                                Credit
                              </Button>
                            </Tooltip>

                            <Tooltip title={`Reduce Money (Debit) from ${row.shop.businessName}`}>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleQuickShopAction(row.shop.shopId, 'debit')}
                                sx={{
                                  minWidth: 'auto',
                                  px: 1.5,
                                  py: 0.5,
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)'
                                }}
                                startIcon={<FontAwesomeIcon icon={faMinusCircle} />}
                              >
                                Debit
                              </Button>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}

                {reportData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canAdjust ? 10 : 9} align="center" sx={{ py: 6 }}>
                      <FontAwesomeIcon icon={faReceipt} size="3x" style={{ color: '#cbd5e1', marginBottom: '12px' }} />
                      <Typography variant="h6" color="text.secondary">
                        No wallet transactions found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Try adjusting search keywords, date filters, or pincode.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={paginationInfo.totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}

        {/* Super Admin Action Dialog (Level 1 Only) */}
        <Dialog
          open={openAdminModal}
          onClose={handleCloseAdminModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{ 
            sx: { 
              borderRadius: 4, 
              overflow: 'hidden',
              boxShadow: '0 24px 48px rgba(15, 23, 42, 0.2)' 
            } 
          }}
        >
          {/* Header */}
          <DialogTitle 
            sx={{ 
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', 
              color: '#ffffff',
              py: 2.5,
              px: 3,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box 
                sx={{ 
                  backgroundColor: 'rgba(147, 51, 234, 0.2)', 
                  color: '#c084fc',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FontAwesomeIcon icon={faUserShield} size="lg" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#ffffff' }}>
                  Manual Admin Wallet Action
                </Typography>
              </Box>
            </Box>
            <IconButton 
              onClick={handleCloseAdminModal}
              sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </DialogTitle>

          <form onSubmit={handleProcessAdminAction}>
            <DialogContent sx={{ p: 3 }}>
              {actionError && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{actionError}</Typography>
                </Alert>
              )}

              <Autocomplete
                options={shopsList}
                getOptionLabel={(option) => `${option.merchantId || 'N/A'} | ${option.businessName} (Seller: ${option.sellerName}) - Mob: ${option.mobileNumber || 'N/A'}`}
                value={shopsList.find(s => s.shopId === actionShopId) || null}
                onChange={(event, newValue) => {
                  setActionShopId(newValue ? newValue.shopId : '');
                }}
                filterOptions={(options, state) => {
                  const input = (state.inputValue || '').toLowerCase().trim();
                  if (!input) return options;
                  return options.filter(opt => {
                    const mId = (opt.merchantId || '').toLowerCase();
                    const bName = (opt.businessName || '').toLowerCase();
                    const sName = (opt.sellerName || '').toLowerCase();
                    const mob = (opt.mobileNumber || '').toLowerCase();
                    const city = (opt.city || '').toLowerCase();
                    const pin = String(opt.pincode || '').toLowerCase();
                    return mId.includes(input) || bName.includes(input) || sName.includes(input) || mob.includes(input) || city.includes(input) || pin.includes(input);
                  });
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.shopId} sx={{ py: 1, borderBottom: '1px solid #f1f5f9' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        [{option.merchantId || 'N/A'}] {option.businessName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Seller: {option.sellerName} | Mob: {option.mobileNumber || 'N/A'} | City: {option.city} ({option.pincode}) | Current Balance: ₹{option.advanceFund || 0}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search & Select Target Shop by Merchant ID, Name or Mobile *"
                    required
                    margin="normal"
                    placeholder="Search by Merchant ID (MER-xxx), Shop Name, Seller or Mobile..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
                  />
                )}
                noOptionsText="No matching shops found"
              />

              {/* Selected Target Shop Summary Card */}
              {(() => {
                const selectedShopObj = shopsList.find(s => s.shopId === actionShopId);
                if (!selectedShopObj) return null;
                const currentBal = parseFloat(selectedShopObj.advanceFund || 0);
                const inputAmt = parseFloat(actionAmount) || 0;
                const estimatedBal = actionType === 'credit' ? currentBal + inputAmt : currentBal - inputAmt;

                return (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mt: 1.5,
                      mb: 1.5,
                      borderRadius: 2.5,
                      border: '1px solid #0284c7',
                      backgroundColor: '#f0f9ff'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0369a1', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FontAwesomeIcon icon={faStore} /> Target Merchant Details
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Merchant ID:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>{selectedShopObj.merchantId || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Merchant Name:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{selectedShopObj.sellerName || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Shop Name:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0284c7' }}>{selectedShopObj.businessName || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Mobile Number:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{selectedShopObj.mobileNumber || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Current Balance:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#16a34a' }}>{formatCurrency(currentBal)}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Est. Balance After:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: actionType === 'credit' ? '#16a34a' : '#dc2626' }}>
                          {formatCurrency(estimatedBal)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })()}

              <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
                <Grid item xs={6}>
                  <Button
                    variant={actionType === 'credit' ? 'contained' : 'outlined'}
                    color="success"
                    fullWidth
                    startIcon={<FontAwesomeIcon icon={faPlusCircle} />}
                    onClick={() => setActionType('credit')}
                    sx={{ 
                      fontWeight: 700, 
                      py: 1.3, 
                      borderRadius: 2.5,
                      textTransform: 'none',
                      boxShadow: actionType === 'credit' ? '0 4px 12px rgba(22, 163, 74, 0.3)' : 'none'
                    }}
                  >
                    Add Money (Credit)
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant={actionType === 'debit' ? 'contained' : 'outlined'}
                    color="error"
                    fullWidth
                    startIcon={<FontAwesomeIcon icon={faMinusCircle} />}
                    onClick={() => setActionType('debit')}
                    sx={{ 
                      fontWeight: 700, 
                      py: 1.3, 
                      borderRadius: 2.5,
                      textTransform: 'none',
                      boxShadow: actionType === 'debit' ? '0 4px 12px rgba(220, 38, 38, 0.3)' : 'none'
                    }}
                  >
                    Reduce Money (Debit)
                  </Button>
                </Grid>
              </Grid>

              <TextField
                label={`Amount to ${actionType === 'credit' ? 'Add' : 'Reduce'} (₹)`}
                type="number"
                fullWidth
                required
                margin="normal"
                value={actionAmount}
                onChange={(e) => setActionAmount(e.target.value)}
                placeholder="Enter amount"
                inputProps={{ step: '0.01', min: '0.01' }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5
                  }
                }}
              />

              <TextField
                label="Remarks / Audit Log Description"
                fullWidth
                required
                margin="normal"
                multiline
                rows={3}
                value={actionRemarks}
                onChange={(e) => setActionRemarks(e.target.value)}
                placeholder="e.g. Manual refill per seller request / Adjustment for duplicate debit"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5
                  }
                }}
              />
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2.5, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              <Button 
                onClick={handleCloseAdminModal} 
                disabled={submitting}
                sx={{ 
                  color: '#64748b', 
                  fontWeight: 600, 
                  textTransform: 'none',
                  px: 2.5,
                  borderRadius: 2 
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <FontAwesomeIcon icon={actionType === 'credit' ? faPlusCircle : faMinusCircle} />}
                sx={{ 
                  background: actionType === 'credit' 
                    ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' 
                    : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  color: '#ffffff',
                  fontWeight: 700, 
                  textTransform: 'none',
                  px: 3,
                  py: 1.2,
                  borderRadius: 2.5,
                  boxShadow: actionType === 'credit' 
                    ? '0 4px 14px rgba(22, 163, 74, 0.35)' 
                    : '0 4px 14px rgba(220, 38, 38, 0.35)',
                  '&:hover': {
                    background: actionType === 'credit' 
                      ? 'linear-gradient(135deg, #15803d 0%, #166534 100%)' 
                      : 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)',
                  }
                }}
              >
                {submitting ? 'Processing...' : `Confirm ${actionType === 'credit' ? 'Credit' : 'Reduction'}`}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

      </Box>
    </div>
  );
};

export default ShopWalletReport;
